import axios, {AxiosInstance, AxiosResponse} from "axios";
import router from "@/router";
import store from "@/store";
import {errorMessage, problemOf} from "@/api/errors";
import {serializeParams} from "@/api/params";

declare module "axios" {
    // Must repeat the type parameter list of axios's own declaration to merge with it.
    export interface AxiosRequestConfig<D = any> {
        /**
         * Do not show the error snackbar for this request. The caller handles the failure itself, for example the
         * fulfil dialog (502 `FULFILLMENT_FAILED`) and the partner create form (409 `PARTNER_NAME_TAKEN`).
         * A 401 still redirects to the login page.
         */
        skipErrorToast?: boolean;
    }
}

/** The per-request options every function in `@/api` accepts as its last argument. */
export interface RequestOptions {
    /** See `AxiosRequestConfig.skipErrorToast`. */
    skipErrorToast?: boolean;
}

/**
 * The one axios instance of the REST API. All paths are relative to `/api`, e.g. `http.get("/me")`. Pages should
 * call the typed functions in `@/api` instead of using it directly.
 *
 * Failure handling:
 *  - 401 means the session is gone: it clears the logged-in user in the store (so the menu and the user name disappear and
 *    `App.vue` no longer sends the user away from `/login`), redirects to `/login` (unless already there) and shows nothing.
 *    Only 401 does: a 403 or a 409 shows its `detail` and leaves the user alone.
 *  - any other failure shows the problem `detail` in the snackbar (`"Server error"` when the response has none),
 *    unless the request was made with `skipErrorToast: true`.
 *  - the returned promise still rejects with the original `AxiosError`; read it with `problemOf` / `isProblem` from `@/api/errors`.
 *
 * `store` and `router` are only touched inside the interceptor, never at import time, because both of them (through
 * `store.loadUser` and the page components) import this module again.
 */
export const http: AxiosInstance = axios.create({
    baseURL: (process.env.VUE_APP_API_URL ?? "") + "/api",
    withCredentials: true,
    paramsSerializer: serializeParams,
});

http.interceptors.response.use(
    response => response,
    (error: unknown) => {
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        if (problemOf(error)?.status === 401) {
            // Before the redirect: with the user still set, the login page would show the user's menu, and App.vue
            // would send a logged-in user straight back from /login.
            store.commit("resetUser");

            if (router.currentRoute.name !== "login") {
                router.push("/login").catch(() => undefined);
            }
        } else if (!(axios.isAxiosError(error) && error.config?.skipErrorToast)) {
            store.commit("setError", errorMessage(error));
        }

        return Promise.reject(error);
    }
);

/** `.then(unwrap)` turns an axios response into its body. */
export const unwrap = <T>(response: AxiosResponse<T>): T => response.data;

/** `.then(noContent)` for the endpoints that answer 202/204 without a body. */
export const noContent = (): void => undefined;

export default http;
